import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import authService, {
  AuthUser,
  LoginCredentials,
  AuthResponse,
} from "services/authService";
import { notify } from "utils/notify";
import { trackEvent, identifyUser } from "utils/analytics";

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<AuthResponse | undefined>;
  logout: () => void;
  updateUser: (partial: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error("Error checking authentication status:", error);
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (
    credentials: LoginCredentials,
  ): Promise<AuthResponse | undefined> => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      setUser(response.user);
      identifyUser(response.user.id.toString(), {
        name: response.user.firstName,
        email: response.user.email,
        role: response.user.role,
      });
      trackEvent("login", { method: "email", role: response.user.role });

      notify.success("Successfully logged in!");
      return response; // Return the response so we can check user role
    } catch (error: any) {
      authService.logout();
      let errorMsg =
        "Failed to login. Please check your credentials and try again.";

      // Extract error message from API response if available
      if (error.response) {
        const serverError =
          error.response.data?.message || error.response.data?.error;
        if (serverError) {
          errorMsg = serverError;
        } else if (error.response.data?.recaptchaError) {
          errorMsg = "reCAPTCHA verification failed. Please try again.";
        }
      }

      trackEvent("login_failed", { error: errorMsg });
      notify.error(errorMsg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      if (!currentUser) {
        throw new Error("No user found");
      }
      identifyUser(currentUser.id.toString(), {
        name: currentUser.firstName,
        email: currentUser.email,
        role: currentUser.role,
      });
    } catch (error) {
      console.error("Error refreshing user:", error);
      authService.logout();
      notify.error("Failed to refresh user. Please log in again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = (): void => {
    trackEvent("logout");
    authService.logout();
    setUser(null);
    notify.info("You have been logged out");
  };

  const updateUser = (partial: Partial<AuthUser>): void => {
    const updated = authService.updateStoredUser(partial);
    if (updated) {
      setUser(updated);
      identifyUser(updated.id.toString(), {
        name: updated.firstName,
        email: updated.email,
        role: updated.role,
      });
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
